import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUp } from "lucide-react";
import { MENU } from "@/data";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const scrollTo = (id) => (e) => {
  e.preventDefault();
  const el = document.getElementById(`menu-cat-${id}`);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -120 });
  else el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const ItemRow = ({ item, sectionId }) => (
  <li
    className="group py-4 border-b border-zinc-800/70 hover:border-ember/40 transition-colors duration-300"
    data-testid={`menu-item-${slug(item.name)}`}
  >
    <div className="flex items-baseline gap-4">
      <h4 className="font-body font-semibold text-lg text-ink group-hover:text-ember transition-colors duration-300">
        {item.name}
      </h4>
      {item.price && (
        <>
          <span className="flex-1 border-b border-dotted border-zinc-800 group-hover:border-ember/30 transition-colors duration-300" aria-hidden="true" />
          <span className="font-mono text-sm text-ember whitespace-nowrap">{item.price}</span>
        </>
      )}
    </div>
    {item.desc && <p className="mt-1 text-base text-zinc-400 leading-relaxed">{item.desc}</p>}
  </li>
);

export default function MenuSection({ standalone = false }) {
  const [activeId, setActiveId] = useState(MENU[0].id);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveId(entry.target.id.replace("menu-cat-", ""));
        });
      },
      { rootMargin: "-25% 0px -65% 0px" }
    );
    MENU.forEach((sec) => {
      const el = document.getElementById(`menu-cat-${sec.id}`);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  return (
    <section id="menu" className="max-w-7xl mx-auto px-6 py-28 lg:py-40" data-testid="menu-section">
      {/* Sticky category bar — always on screen while browsing the menu (mobile) */}
      <div
        className={`lg:hidden sticky ${standalone ? "top-0" : "top-16"} z-30 -mx-6 px-6 mb-12 py-3 bg-coal-900/90 backdrop-blur-xl border-y border-zinc-800 overflow-x-auto`}
        data-testid="menu-sticky-bar"
      >
        <div className="flex gap-2 w-max">
          {MENU.map((sec) => (
            <a
              key={sec.id}
              href={`#menu-cat-${sec.id}`}
              onClick={scrollTo(sec.id)}
              data-testid={`menu-chip-${sec.id}`}
              className={`font-mono text-[11px] tracking-[0.15em] uppercase px-4 py-2 border whitespace-nowrap transition-colors duration-300 ${
                activeId === sec.id
                  ? "border-ember text-ember bg-ember/10"
                  : "border-zinc-800 text-zinc-500"
              }`}
            >
              {sec.title}
            </a>
          ))}
        </div>
      </div>

      <div className="grid lg:grid-cols-[26%_1fr] gap-12 lg:gap-20">
        <div className={`hidden lg:block lg:sticky ${standalone ? "lg:top-8" : "lg:top-28"} self-start`}>
          <p className="font-mono text-xs tracking-[0.3em] uppercase text-ember mb-4">The Feed</p>
          <h2 className="font-display text-6xl sm:text-7xl leading-none uppercase mb-10 text-gold-shine" data-testid="menu-heading">
            Menu
          </h2>
          <nav className="flex flex-col gap-2" data-testid="menu-category-nav">
            {MENU.map((sec, i) => (
              <a
                key={sec.id}
                href={`#menu-cat-${sec.id}`}
                onClick={scrollTo(sec.id)}
                data-testid={`menu-nav-${sec.id}`}
                className={`group flex items-center gap-3 font-mono text-xs tracking-[0.2em] uppercase transition-colors duration-300 ${
                  activeId === sec.id ? "text-ember" : "text-zinc-500 hover:text-ember"
                }`}
              >
                <span className={`transition-colors duration-300 ${activeId === sec.id ? "text-ember" : "text-zinc-700 group-hover:text-ember"}`}>
                  {String(i + 1).padStart(2, "0")}
                </span>
                {sec.title}
                {activeId === sec.id && <span className="w-1.5 h-1.5 bg-ember rotate-45" aria-hidden="true" />}
              </a>
            ))}
          </nav>
        </div>

        <div className="lg:hidden mb-4">
          <p className="font-mono text-xs tracking-[0.3em] uppercase text-ember mb-2">The Feed</p>
          <h2 className="font-display text-6xl leading-none uppercase text-gold-shine">Menu</h2>
        </div>

        <div className="space-y-24">
          {MENU.map((sec, i) => (
            <motion.div
              key={sec.id}
              id={`menu-cat-${sec.id}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, ease: "circOut" }}
              data-testid={`menu-category-${sec.id}`}
            >
              <div className="flex items-baseline gap-4 mb-2">
                <span className="font-mono text-sm text-ember">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="font-display text-4xl sm:text-5xl uppercase tracking-wide">{sec.title}</h3>
              </div>
              {sec.blurb && <p className="text-sm text-zinc-400 mb-3 max-w-xl">{sec.blurb}</p>}
              {sec.notes?.map((n) => (
                <p key={n} className="font-mono text-xs tracking-[0.15em] uppercase text-ember font-bold mb-1">{n}</p>
              ))}

              {sec.items?.length > 0 && (
                <ul className="mt-6 lg:grid lg:grid-cols-2 lg:gap-x-12">
                  {sec.items.map((item) => (
                    <ItemRow key={item.name} item={item} sectionId={sec.id} />
                  ))}
                </ul>
              )}

              {sec.groups?.map((g) => (
                <div key={g.label} className="mt-8">
                  <div className="flex items-baseline gap-4">
                    <h4 className="font-mono text-xs tracking-[0.25em] uppercase text-ink">{g.label}</h4>
                    {g.desc && <span className="text-xs text-zinc-500">{g.desc}</span>}
                  </div>
                  <ul className="mt-2 lg:grid lg:grid-cols-2 lg:gap-x-12">
                    {g.items.map((item) => (
                      <ItemRow key={`${g.label}-${item.name}`} item={item} sectionId={sec.id} />
                    ))}
                  </ul>
                </div>
              ))}
            </motion.div>
          ))}

          <div className="flex flex-wrap items-center justify-between gap-4">
            <p className="font-mono text-xs tracking-[0.2em] uppercase text-zinc-600">
              Prices in AUD · Halal options available
            </p>
            <a
              href="#menu"
              onClick={(e) => {
                e.preventDefault();
                const el = document.getElementById("menu");
                if (window.__lenis) window.__lenis.scrollTo(el, { offset: -80 });
                else el?.scrollIntoView({ behavior: "smooth" });
              }}
              data-testid="menu-back-to-top"
              className="inline-flex items-center gap-2 font-mono text-xs tracking-[0.2em] uppercase text-ember hover:text-ink transition-colors duration-300"
            >
              <ArrowUp className="w-4 h-4" /> Back to menu index
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
